import type { ButtonProps } from "antd";
import { Button, Space, Tag, Typography } from "antd";
import type { AdminLegendGroup, AdminSummaryBarItem } from "./admin-legend-types";
import {
  getLegendBadgeColor,
  getLegendSemanticPresentation,
  type LegendBadgeStatus,
} from "./semantic-legend";

function renderAdminLegendMarker({
  active,
  badgeStatus,
  color,
}: {
  active: boolean;
  badgeStatus: LegendBadgeStatus;
  color?: string | undefined;
}) {
  if (color) {
    return (
      <Tag
        aria-hidden
        color={color}
        style={{
          width: 8,
          height: 8,
          minWidth: 8,
          borderRadius: "50%",
          lineHeight: "8px",
          paddingInline: 0,
          marginInlineEnd: 0,
          border: "none",
        }}
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        display: "inline-block",
        backgroundColor: active ? "var(--ant-colorWhite, #fff)" : getLegendBadgeColor(badgeStatus),
        border: active ? "1px solid rgba(255, 255, 255, 0.7)" : "none",
      }}
    />
  );
}

function resolveAdminLegendButtonColor(item: {
  active?: boolean | undefined;
  color?: string | undefined;
}): NonNullable<ButtonProps["color"]> {
  return (item.color ?? (item.active ? "primary" : "default")) as NonNullable<ButtonProps["color"]>;
}

export function AdminSummaryBar({ items }: { items: AdminSummaryBarItem[] }) {
  if (items.length === 0) {
    return null;
  }

  const normalizedItems = items.map((item) => {
    if (item.label !== undefined || item.count !== undefined) {
      return item;
    }

    const rawValue = item.value;
    if (typeof rawValue !== "string") {
      return { ...item, value: <Typography.Text>{rawValue}</Typography.Text> };
    }

    const normalized = rawValue.trim();
    const separatorIndex = normalized.search(/[:：]/);
    if (separatorIndex <= 0 || separatorIndex >= normalized.length - 1) {
      return { ...item, value: <Typography.Text>{rawValue}</Typography.Text> };
    }

    const label = normalized.slice(0, separatorIndex).trim();
    const value = normalized.slice(separatorIndex + 1).trim();
    if (!label || !value) {
      return { ...item, value: <Typography.Text>{rawValue}</Typography.Text> };
    }

    return {
      ...item,
      value: (
        <div className="admin-summary-item-content">
          <Typography.Text type="secondary" className="admin-summary-item-label">
            {label}
          </Typography.Text>
          <Typography.Text className="admin-summary-item-value">{value}</Typography.Text>
        </div>
      ),
    };
  });

  const isInteractive = normalizedItems.some(
    (item) => item.label !== undefined || item.count !== undefined,
  );

  if (isInteractive) {
    return (
      <Space size={[12, 12]} wrap>
        {normalizedItems.map((item) => {
          const legendPresentation = getLegendSemanticPresentation({
            key: item.key,
            semantic: item.semantic,
          });
          const displayLabel =
            item.label ?? legendPresentation.unresolvedLabel ?? legendPresentation.label;
          const isActive = Boolean(item.onClick && item.active);
          const body = (
            <Space size={8}>
              {renderAdminLegendMarker({
                active: isActive,
                badgeStatus: legendPresentation.badgeStatus,
                color: item.color,
              })}
              <Typography.Text
                style={{
                  color: isActive
                    ? "rgba(255, 255, 255, 0.88)"
                    : "var(--ant-colorTextSecondary, rgba(0, 0, 0, 0.65))",
                }}
              >
                {displayLabel}
              </Typography.Text>
              <Typography.Text
                strong
                style={{
                  color: isActive
                    ? "var(--ant-colorTextLightSolid, #fff)"
                    : "var(--ant-colorText, rgba(0, 0, 0, 0.88))",
                }}
              >
                {item.count}
              </Typography.Text>
            </Space>
          );

          if (item.onClick) {
            return (
              <Button
                key={item.key}
                color={resolveAdminLegendButtonColor(item)}
                variant={item.active ? "solid" : "outlined"}
                onClick={item.onClick}
              >
                {body}
              </Button>
            );
          }

          return (
            <div
              key={item.key}
              style={{
                border: "1px solid var(--ant-colorBorderSecondary, #f0f0f0)",
                borderRadius: 8,
                padding: "8px 12px",
                background: "var(--ant-colorBgContainer, #fff)",
              }}
            >
              {body}
            </div>
          );
        })}
      </Space>
    );
  }

  return (
    <Space size={[12, 12]} wrap>
      {normalizedItems.map((item) => (
        <div
          key={item.key}
          style={{
            border: "1px solid var(--ant-colorBorderSecondary, #f0f0f0)",
            borderRadius: 8,
            padding: "8px 12px",
            background: "var(--ant-colorBgContainer, #fff)",
          }}
        >
          {item.value}
        </div>
      ))}
    </Space>
  );
}

export function AdminLegendGroups({ groups }: { groups: AdminLegendGroup[] }) {
  if (groups.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        border: "1px solid var(--ant-colorBorderSecondary, #f0f0f0)",
        borderRadius: 12,
        padding: "12px 16px",
        background: "var(--ant-colorBgContainer, #fff)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 12,
        }}
      >
        {groups.map((group) => (
          <div
            key={group.key}
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            <Typography.Text strong style={{ whiteSpace: "nowrap" }}>
              {group.title}
            </Typography.Text>
            <Space size={[12, 12]} wrap>
              {group.items.map((item) => {
                const legendPresentation = getLegendSemanticPresentation({
                  key: item.key,
                  semantic: item.semantic,
                });
                const displayLabel =
                  item.label ?? legendPresentation.unresolvedLabel ?? legendPresentation.label;
                const isActive = Boolean(item.onClick && item.active);
                const body = (
                  <Space size={8}>
                    {renderAdminLegendMarker({
                      active: isActive,
                      badgeStatus: legendPresentation.badgeStatus,
                      color: item.color,
                    })}
                    <Typography.Text
                      style={{
                        color: isActive
                          ? "rgba(255, 255, 255, 0.88)"
                          : "var(--ant-colorTextSecondary, rgba(0, 0, 0, 0.65))",
                      }}
                    >
                      {displayLabel}
                    </Typography.Text>
                    <Typography.Text
                      strong
                      style={{
                        color: isActive
                          ? "var(--ant-colorTextLightSolid, #fff)"
                          : "var(--ant-colorText, rgba(0, 0, 0, 0.88))",
                      }}
                    >
                      {item.count}
                    </Typography.Text>
                  </Space>
                );

                if (item.onClick) {
                  return (
                    <Button
                      key={item.key}
                      color={resolveAdminLegendButtonColor(item)}
                      variant={item.active ? "solid" : "outlined"}
                      onClick={item.onClick}
                    >
                      {body}
                    </Button>
                  );
                }

                return (
                  <div
                    key={item.key}
                    style={{
                      border: "1px solid var(--ant-colorBorderSecondary, #f0f0f0)",
                      borderRadius: 8,
                      padding: "8px 12px",
                      background: "var(--ant-colorBgLayout, #fafafa)",
                    }}
                  >
                    {body}
                  </div>
                );
              })}
            </Space>
          </div>
        ))}
      </div>
    </div>
  );
}
